import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { ArrowLeft, FileText, MapPin, AlertTriangle, Save, Send, Camera, Paperclip } from 'lucide-react';
import { useStore } from '../data/store';
import { toast } from 'sonner';
import { Role } from '../data/rbac';
import { BINDA_LIST } from '../data/binda';
export default function ReportCreate() {
  const navigate = useNavigate();
  const createReport = useStore(s => s.createReport);
  const submitReport = useStore(s => s.submitReport);
  const role = localStorage.getItem('bimcs_role') as Role || 'Super Admin Nasional';
  const [formData, setFormData] = useState({
    title: '',
    type: 'Laporan Informasi',
    category: 'Keamanan',
    urgency: 'Sedang',
    region: 'DKI Jakarta',
    kabupaten: '',
    kecamatan: '',
    ringkasan: '',
    kronologi: '',
    pihakTerkait: '',
    potensiDampak: '',
    rekomendasiAwal: ''
  });
  const [attachments, setAttachments] = useState<{
    name: string;
    kind: 'foto' | 'dokumen';
  }[]>([]);
  const [geotag, setGeotag] = useState<{
    lat: number;
    lng: number;
    label: string;
  } | null>(null);
  const handleCaptureGps = () => {
    // Simulasi penangkapan koordinat GPS perangkat lapangan.
    const lat = -6.2 + (Math.random() - 0.5) * 0.2;
    const lng = 106.8 + (Math.random() - 0.5) * 0.2;
    setGeotag({
      lat: Number(lat.toFixed(5)),
      lng: Number(lng.toFixed(5)),
      label: `${formData.region}${formData.kabupaten ? ', ' + formData.kabupaten : ''}`
    });
    toast.success('Koordinat GPS berhasil diambil.');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleAddAttachment = () => {
    setAttachments([...attachments, {
      name: `lampiran_${attachments.length + 1}.jpg`,
      kind: 'foto'
    }]);
    toast.success('Lampiran berhasil ditambahkan.');
  };
  const handleSaveDraft = () => {
    if (!formData.title) {
      toast.error('Judul laporan wajib diisi.');
      return;
    }
    createReport({
      ...formData,
      sender: role === 'Personel Lapangan' ? 'OP-772' : 'Admin',
      offline: true,
      attachments,
      geotag: geotag ?? undefined
    });
    toast.success('Draf laporan berhasil disimpan.');
    navigate('/reports');
  };
  const handleSubmit = () => {
    if (!formData.title || !formData.ringkasan) {
      toast.error('Judul dan ringkasan laporan wajib diisi.');
      return;
    }
    const report = createReport({
      ...formData,
      sender: role === 'Personel Lapangan' ? 'OP-772' : 'Admin',
      offline: false,
      attachments,
      geotag: geotag ?? undefined
    });
    submitReport(report.id);
    toast.success('Laporan berhasil dikirim untuk verifikasi.');
    navigate('/reports');
  };
  return <div className="space-y-6 animate-in fade-in duration-500 max-w-5xl mx-auto pb-12" data-id="element-3418">
      <div className="flex items-center gap-4 border-b border-border/50 pb-4" data-id="element-3419">
        <Button variant="ghost" size="icon" onClick={() => navigate('/reports')} className="hover:bg-primary/20 hover:text-primary" data-id="element-3420">
          <ArrowLeft className="w-5 h-5" data-id="element-3421" />
        </Button>
        <div data-id="element-3422">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground flex items-center gap-3" data-id="element-3423">
            ENTRI LAPORAN BARU
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-3424">
            DENS CAKRA // Formulir Intelijen Lapangan
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3425">
        <div className="lg:col-span-2 space-y-6" data-id="element-3426">
          <Card className="glass-panel border-border/50" data-id="element-3427">
            <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-3428">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3429">
                <FileText className="w-4 h-4 text-primary" data-id="element-3430" /> Informasi Dasar
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4" data-id="element-3431">
              <div className="space-y-2" data-id="element-3432">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3433">
                  Judul Informasi *
                </label>
                <input name="title" value={formData.title} onChange={handleChange} className="w-full bg-black/40 border border-border/50 rounded p-3 text-sm font-mono focus:outline-none focus:border-primary/50" placeholder="Contoh: Pergerakan Massa Tidak Dikenal di Sektor 4" data-id="element-3434" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-3435">
                <div className="space-y-2" data-id="element-3436">
                  <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3437">
                    Jenis Input
                  </label>
                  <select name="type" value={formData.type} onChange={handleChange} className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50" data-id="element-3438">
                    <option data-id="element-3439">Observasi Lapangan</option>
                    <option data-id="element-3440">Informasi Warga</option>
                    <option data-id="element-3441">Kejadian Langsung</option>
                    <option data-id="element-3442">Patroli Rutin</option>
                  </select>
                </div>
                <div className="space-y-2" data-id="element-3443">
                  <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3444">
                    Kategori Kejadian
                  </label>
                  <select name="category" value={formData.category} onChange={handleChange} className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50" data-id="element-3445">
                    {['Politik', 'Sosial', 'Ekonomi', 'Keamanan', 'Siber', 'Konflik Sosial', 'Infrastruktur Strategis', 'Objek Vital Nasional', 'Bencana', 'Lintas Negara'].map(c => <option key={c} value={c} data-id="element-3446">
                        {c}
                      </option>)}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-panel border-border/50" data-id="element-3447">
            <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-3448">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3449">
                <FileText className="w-4 h-4 text-primary" data-id="element-3450" /> Narasi Lapangan
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4" data-id="element-3451">
              <div className="space-y-2" data-id="element-3452">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3453">
                  Deskripsi / Observasi *
                </label>
                <textarea name="ringkasan" value={formData.ringkasan} onChange={handleChange} className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[120px]" placeholder="Jelaskan apa yang dilihat, didengar, atau ditemukan di lapangan..." data-id="element-3454" />
              </div>
              <div className="space-y-2" data-id="element-3455">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3456">
                  Kronologi Kejadian (Opsional)
                </label>
                <textarea name="kronologi" value={formData.kronologi} onChange={handleChange} className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[100px]" placeholder="Urutan waktu kejadian secara detail..." data-id="element-3457" />
              </div>
              <div className="space-y-2" data-id="element-3458">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3459">
                  Pihak Terkait / Aktor (Opsional)
                </label>
                <textarea name="pihakTerkait" value={formData.pihakTerkait} onChange={handleChange} className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[60px]" placeholder="Individu atau kelompok yang terlibat..." data-id="element-3460" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6" data-id="element-3461">
          <Card className="glass-panel border-border/50" data-id="element-3462">
            <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-3463">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3464">
                <AlertTriangle className="w-4 h-4 text-primary" data-id="element-3465" /> Klasifikasi
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4" data-id="element-3466">
              <div className="space-y-2" data-id="element-3467">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3468">
                  Tingkat Urgensi
                </label>
                <select name="urgency" value={formData.urgency} onChange={handleChange} className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50" data-id="element-3469">
                  <option value="Rendah" data-id="element-3470">Rendah</option>
                  <option value="Sedang" data-id="element-3471">Sedang</option>
                  <option value="Tinggi" data-id="element-3472">Tinggi</option>
                  <option value="Kritis" data-id="element-3473">Kritis</option>
                </select>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-panel border-border/50" data-id="element-3474">
            <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-3475">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3476">
                <MapPin className="w-4 h-4 text-primary" data-id="element-3477" /> Lokasi
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4" data-id="element-3478">
              <div className="space-y-2" data-id="element-3479">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3480">
                  Provinsi / BINDA
                </label>
                <select name="region" value={formData.region} onChange={handleChange} className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50" data-id="element-3481">
                  {BINDA_LIST.map(b => <option key={b.provinsi} value={b.provinsi} data-id="element-3482">
                      {b.provinsi}
                    </option>)}
                </select>
              </div>
              <div className="space-y-2" data-id="element-3483">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3484">
                  Kabupaten / Kota
                </label>
                <input name="kabupaten" value={formData.kabupaten} onChange={handleChange} className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50" placeholder="Opsional" data-id="element-3485" />
              </div>
              <div className="space-y-2" data-id="element-3486">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3487">
                  Kecamatan
                </label>
                <input name="kecamatan" value={formData.kecamatan} onChange={handleChange} className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50" placeholder="Opsional" data-id="element-3488" />
              </div>
              <Button variant="outline" onClick={handleCaptureGps} className="w-full text-xs font-mono border-border/50" data-id="element-3489">
                <MapPin className="w-3 h-3 mr-2" data-id="element-3490" /> Ambil Koordinat GPS
              </Button>
              {geotag && <div className="flex items-center gap-2 text-[10px] font-mono text-primary bg-primary/10 p-2 rounded border border-primary/20" data-id="element-3491">
                  <MapPin className="w-3 h-3 shrink-0" data-id="element-3492" />
                  <span data-id="element-3493">
                    {geotag.lat}, {geotag.lng}
                  </span>
                </div>}
            </CardContent>
          </Card>

          <Card className="glass-panel border-border/50" data-id="element-3494">
            <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-3495">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3496">
                <Paperclip className="w-4 h-4 text-primary" data-id="element-3497" /> Lampiran
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4" data-id="element-3498">
              <div onClick={handleAddAttachment} className="border-2 border-dashed border-border/50 rounded-lg p-6 flex flex-col items-center justify-center text-center hover:bg-white/5 transition-colors cursor-pointer" data-id="element-3499">
                <Camera className="w-8 h-8 text-muted-foreground mb-2" data-id="element-3500" />
                <span className="text-xs font-mono text-muted-foreground" data-id="element-3501">
                  Klik untuk menambah lampiran
                </span>
                <span className="text-[10px] font-mono text-muted-foreground/50 mt-1" data-id="element-3502">
                  Mendukung JPG, PNG, PDF, MP4
                </span>
              </div>
              {attachments.length > 0 && <div className="mt-4 space-y-2" data-id="element-3503">
                  {attachments.map((att, i) => <div key={i} className="flex items-center gap-2 text-xs font-mono bg-black/40 p-2 rounded border border-border/50" data-id="element-3504">
                      <Paperclip className="w-3 h-3 text-primary" data-id="element-3505" />
                      {att.name}
                    </div>)}
                </div>}
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3 pt-4" data-id="element-3506">
            <Button onClick={handleSubmit} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs tracking-widest uppercase py-6" data-id="element-3507">
              <Send className="w-4 h-4 mr-2" data-id="element-3508" /> Kirim Laporan
            </Button>
            <Button variant="outline" onClick={handleSaveDraft} className="w-full font-mono text-xs tracking-widest uppercase border-border/50" data-id="element-3509">
              <Save className="w-4 h-4 mr-2" data-id="element-3510" /> Simpan Draf
            </Button>
          </div>
        </div>
      </div>
    </div>;
}